import React, { FC, useState, useCallback, useMemo } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Pagination,
  Alert,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  GetApp as DownloadIcon,
} from '@mui/icons-material'
import { ActionButton } from '@/apps/legacy/components/portal/CustomButton'
import { PharmacySelector } from '@/apps/legacy/components/portal/PharmacySelector'
import { useNavigate } from 'react-router-dom'
import { LoadingIndicator } from '@/apps/legacy/components/atoms/LoadingIndicator'
import { PortalLayout } from '@/apps/legacy/components/portal/PortalLayout'
import { PortalPageHeader } from '@/apps/legacy/components/portal/PortalPageHeader'
import { SECTION_HEADER_STYLES } from '@/apps/legacy/shared/constants/styles'
import {
  useMetricsRecordsQuery,
  usePharmacyListQuery,
  usePharmacyUrlParam,
} from '@/features/internal/metrics'
import { useExportMetricsRecordsMutation } from '@/features/internal'
import {
  MetricsFilters,
  MetricsSortField,
  MetricsSortOptions,
  MetricsExportRequest,
} from '@/features/internal/metrics/types/metrics'
import dayjs from 'dayjs'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'

const METRICS_CONFIG = {
  PAGE_SIZE: 20,
  PRESCRIPTION_NUMBER_DISPLAY_LENGTH: 6,
} as const

export const MetricsRecordsPage: FC = () => {
  const navigate = useNavigate()
  const { pharmacyId: selectedPharmacyId, updatePharmacyParam } =
    usePharmacyUrlParam()
  const [currentPage, setCurrentPage] = useState(1)
  const exportMutation = useExportMetricsRecordsMutation()

  // 入力中のフィルター値（まだ適用されていない）
  const [tempFilters, setTempFilters] = useState<MetricsFilters>({
    search: '',
    startDate: '',
    endDate: '',
    minMedicationCount: null,
    maxMedicationCount: null,
    hasAudio: null,
    hasSoap: null,
    soapCopied: null,
  })

  // 実際に適用されるフィルター値
  const [appliedFilters, setAppliedFilters] = useState<MetricsFilters>({
    search: '',
    startDate: '',
    endDate: '',
    minMedicationCount: null,
    maxMedicationCount: null,
    hasAudio: null,
    hasSoap: null,
    soapCopied: null,
  })

  const [sort, setSort] = useState<MetricsSortOptions>({
    field: 'created_at',
    direction: 'desc',
  })

  // 薬局リスト取得
  const { pharmacyData } = usePharmacyListQuery()

  const formatDateTime = useCallback((dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    try {
      const date = new Date(dateStr)
      if (isNaN(date.getTime())) return '-'
      return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:${date
        .getMinutes()
        .toString()
        .padStart(2, '0')}`
    } catch {
      return '-'
    }
  }, [])

  const formatDuration = useCallback((seconds: number | null | undefined) => {
    if (seconds == null || seconds === 0) return '-'
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}分${remainingSeconds}秒`
  }, [])

  // クエリパラメータを構築
  const queryParams = useMemo(() => {
    if (!pharmacyData) return null

    const dateRange =
      appliedFilters.startDate || appliedFilters.endDate
        ? {
            start: appliedFilters.startDate || '2020-01-01',
            end:
              appliedFilters.endDate || new Date().toISOString().split('T')[0],
          }
        : undefined

    const params = {
      page: currentPage,
      page_size: METRICS_CONFIG.PAGE_SIZE,
      ...(dateRange && { date_range: dateRange }),
      ...(appliedFilters.search && { search: appliedFilters.search }),
      filters: {
        ...(appliedFilters.hasAudio !== null && {
          has_audio: appliedFilters.hasAudio,
        }),
        ...(appliedFilters.hasSoap !== null && {
          has_soap: appliedFilters.hasSoap,
        }),
        ...(appliedFilters.soapCopied !== null && {
          soap_copied: appliedFilters.soapCopied,
        }),
        ...(appliedFilters.minMedicationCount && {
          medication_count_min: appliedFilters.minMedicationCount,
        }),
        ...(appliedFilters.maxMedicationCount && {
          medication_count_max: appliedFilters.maxMedicationCount,
        }),
      },
      sort: {
        field: sort.field,
        order: sort.direction,
      },
      // 未選択は「絞らない」= キーを送らない。本部なら全社、それ以外は自店になる
      pharmacy_ids:
        selectedPharmacyId && selectedPharmacyId !== ''
          ? [selectedPharmacyId]
          : undefined,
    }

    return params
  }, [pharmacyData, currentPage, sort, appliedFilters, selectedPharmacyId])

  // メトリクスレコード取得
  const {
    data: recordsData,
    isLoading,
    error: queryError,
    refetch,
  } = useMetricsRecordsQuery({
    params: queryParams!,
    enabled: !!queryParams,
  })

  const metrics = recordsData?.records ?? []
  const totalCount = recordsData?.total ?? 0
  const loading = isLoading || exportMutation.isPending
  const error = queryError?.message ?? null

  const handleSearch = () => {
    // 入力中のフィルターを適用
    setAppliedFilters(tempFilters)
    setCurrentPage(1)
  }

  const handleReset = () => {
    const resetFilters = {
      search: '',
      startDate: '',
      endDate: '',
      minMedicationCount: null,
      maxMedicationCount: null,
      hasAudio: null,
      hasSoap: null,
      soapCopied: null,
    }
    setTempFilters(resetFilters)
    setAppliedFilters(resetFilters)
    setCurrentPage(1)
  }

  const handleExportCSV = async () => {
    const filters = appliedFilters
    const dateRange =
      filters.startDate || filters.endDate
        ? {
            start: filters.startDate || '2020-01-01',
            end: filters.endDate || new Date().toISOString().split('T')[0],
          }
        : undefined

    const exportParams: MetricsExportRequest = {
      format: 'csv' as const,
      ...(dateRange && { date_range: dateRange }),
      ...(filters.search && { search: filters.search }),
      filters: {
        ...(filters.hasAudio !== null && { has_audio: filters.hasAudio }),
        ...(filters.hasSoap !== null && { has_soap: filters.hasSoap }),
        ...(filters.soapCopied !== null && {
          soap_copied: filters.soapCopied,
        }),
        ...(filters.minMedicationCount && {
          medication_count_min: filters.minMedicationCount,
        }),
        ...(filters.maxMedicationCount && {
          medication_count_max: filters.maxMedicationCount,
        }),
      },
    }

    // 薬局IDを追加
    if (selectedPharmacyId && selectedPharmacyId !== '') {
      exportParams.pharmacy_ids = [selectedPharmacyId]
    }

    await exportMutation.mutateAsync(exportParams)
  }

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page)
  }

  const handleSortChange = (field: MetricsSortField) => {
    const isSameField = sort.field === field
    const newDirection =
      isSameField && sort.direction === 'desc' ? 'asc' : 'desc'
    setSort({ field, direction: newDirection })
    setCurrentPage(1)
  }

  return (
    <PortalLayout>
      <Box sx={{ position: 'relative' }}>
        {/* ローディング表示（固定位置） */}
        {isLoading && (
          <Box
            sx={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 9999,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: 2,
              p: 2,
            }}
          >
            <LoadingIndicator />
          </Box>
        )}

        <PortalPageHeader title="指導実績一覧" />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* フィルター・ソート・表示オプション */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            backgroundColor: colorTokens.background.paper,
            borderRadius: 2,
            border: `1px solid ${colorTokens.border.light}`,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            p: 3,
          }}
        >
          {/* 基本フィルター */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
              mb: 3,
            }}
          >
            <Box sx={{ minWidth: 200 }}>
              <PharmacySelector
                value={selectedPharmacyId}
                onChange={(value) => {
                  updatePharmacyParam(value)
                  setCurrentPage(1)
                }}
                pharmacyData={pharmacyData}
                size="small"
                label="店舗"
                showAllOption={true} // 指導実績一覧では全社選択を許可
              />
            </Box>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="開始日"
                value={
                  tempFilters.startDate ? dayjs(tempFilters.startDate) : null
                }
                onChange={(date) =>
                  setTempFilters({
                    ...tempFilters,
                    startDate: date ? date.format('YYYY-MM-DD') : '',
                  })
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 200 },
                  },
                }}
              />
            </LocalizationProvider>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="終了日"
                value={tempFilters.endDate ? dayjs(tempFilters.endDate) : null}
                onChange={(date) =>
                  setTempFilters({
                    ...tempFilters,
                    endDate: date ? date.format('YYYY-MM-DD') : '',
                  })
                }
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: { width: 200 },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          {/* 詳細フィルター */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                color: colorTokens.text.secondary,
              }}
            >
              詳細フィルター
            </Typography>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={12}>
                <TextField
                  fullWidth
                  label="医療機関名または処方箋番号で検索"
                  value={tempFilters.search}
                  onChange={(e) =>
                    setTempFilters({ ...tempFilters, search: e.target.value })
                  }
                  size="small"
                  InputProps={{
                    endAdornment: <SearchIcon />,
                  }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="薬剤数（以上）"
                  value={tempFilters.minMedicationCount || ''}
                  onChange={(e) =>
                    setTempFilters({
                      ...tempFilters,
                      minMedicationCount: e.target.value
                        ? parseInt(e.target.value)
                        : null,
                    })
                  }
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="薬剤数（以下）"
                  value={tempFilters.maxMedicationCount || ''}
                  onChange={(e) =>
                    setTempFilters({
                      ...tempFilters,
                      maxMedicationCount: e.target.value
                        ? parseInt(e.target.value)
                        : null,
                    })
                  }
                  size="small"
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={tempFilters.hasAudio === true}
                    onChange={(e) =>
                      setTempFilters({
                        ...tempFilters,
                        hasAudio: e.target.checked ? true : null,
                      })
                    }
                  />
                }
                label="音声録音あり"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={tempFilters.hasSoap === true}
                    onChange={(e) =>
                      setTempFilters({
                        ...tempFilters,
                        hasSoap: e.target.checked ? true : null,
                      })
                    }
                  />
                }
                label="SOAP生成済み"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={tempFilters.soapCopied === true}
                    onChange={(e) =>
                      setTempFilters({
                        ...tempFilters,
                        soapCopied: e.target.checked ? true : null,
                      })
                    }
                  />
                }
                label="SOAPコピー済"
              />
            </Box>
          </Box>

          {/* アクションボタン */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                sx={{
                  color: colorTokens.blue[600],
                  borderColor: colorTokens.blue[600],
                  minHeight: 40,
                  py: 1.5,
                  px: 2,
                  borderRadius: 2,
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  '&:hover': {
                    borderColor: colorTokens.blue[700],
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  },
                }}
              >
                検索
              </Button>
              <Button
                variant="outlined"
                onClick={handleReset}
                sx={{
                  color: colorTokens.grey[500],
                  borderColor: colorTokens.border.light,
                  minHeight: 40,
                  py: 1.5,
                  px: 2,
                  borderRadius: 2,
                  backgroundColor: 'rgba(158, 158, 158, 0.12)',
                  '&:hover': {
                    borderColor: colorTokens.grey[500],
                    backgroundColor: 'rgba(158, 158, 158, 0.18)',
                  },
                }}
              >
                リセット
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* データ一覧 */}
        <Card
          sx={{
            backgroundColor: colorTokens.background.paper,
            borderRadius: 2,
            border: `1px solid ${colorTokens.border.light}`,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="h6" sx={SECTION_HEADER_STYLES}>
                  データ一覧
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: colorTokens.text.secondary,
                  }}
                >
                  全{totalCount}件中{' '}
                  {totalCount > 0
                    ? (currentPage - 1) * METRICS_CONFIG.PAGE_SIZE + 1
                    : 0}
                  -
                  {Math.min(currentPage * METRICS_CONFIG.PAGE_SIZE, totalCount)}
                  件を表示
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => refetch()}
                  disabled={loading}
                  sx={{
                    color: colorTokens.grey[500],
                    borderColor: colorTokens.border.light,
                    minHeight: 40,
                    py: 1.5,
                    px: 2,
                    borderRadius: 2,
                    backgroundColor: 'rgba(158, 158, 158, 0.12)',
                    '&:hover': {
                      borderColor: colorTokens.grey[500],
                      backgroundColor: 'rgba(158, 158, 158, 0.18)',
                    },
                  }}
                >
                  更新
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportCSV}
                  disabled={loading}
                  sx={{
                    color: colorTokens.grey[500],
                    borderColor: colorTokens.border.light,
                    minHeight: 40,
                    py: 1.5,
                    px: 2,
                    borderRadius: 2,
                    backgroundColor: 'rgba(158, 158, 158, 0.12)',
                    '&:hover': {
                      borderColor: colorTokens.grey[500],
                      backgroundColor: 'rgba(158, 158, 158, 0.18)',
                    },
                  }}
                >
                  CSV出力
                </Button>
              </Box>
            </Box>

            {metrics.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="textSecondary">
                  データが見つかりませんでした
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
                <Table
                  sx={{
                    tableLayout: 'fixed',
                    width: '100%',
                    minWidth: '900px',
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: '11%' }}>
                        <TableSortLabel
                          active={sort.field === 'created_at'}
                          direction={
                            sort.field === 'created_at'
                              ? sort.direction
                              : 'desc'
                          }
                          onClick={() => handleSortChange('created_at')}
                        >
                          日時
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ width: '13%' }}>
                        <TableSortLabel
                          active={sort.field === 'pharmacy_name'}
                          direction={
                            sort.field === 'pharmacy_name'
                              ? sort.direction
                              : 'desc'
                          }
                          onClick={() => handleSortChange('pharmacy_name')}
                        >
                          薬局名
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ width: '15%' }}>
                        <TableSortLabel
                          active={sort.field === 'medical_institution'}
                          direction={
                            sort.field === 'medical_institution'
                              ? sort.direction
                              : 'desc'
                          }
                          onClick={() =>
                            handleSortChange('medical_institution')
                          }
                        >
                          医療機関名
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ width: '11%' }}>
                        <TableSortLabel
                          active={sort.field === 'prescription_number'}
                          direction={
                            sort.field === 'prescription_number'
                              ? sort.direction
                              : 'desc'
                          }
                          onClick={() =>
                            handleSortChange('prescription_number')
                          }
                        >
                          処方箋番号
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" sx={{ width: '7%' }}>
                        <TableSortLabel
                          active={sort.field === 'medication_count'}
                          direction={
                            sort.field === 'medication_count'
                              ? sort.direction
                              : 'desc'
                          }
                          onClick={() => handleSortChange('medication_count')}
                        >
                          薬剤
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" sx={{ width: '9%' }}>
                        <TableSortLabel
                          active={sort.field === 'duration'}
                          direction={
                            sort.field === 'duration' ? sort.direction : 'desc'
                          }
                          onClick={() => handleSortChange('duration')}
                        >
                          音声
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" sx={{ width: '7%' }}>
                        <TableSortLabel
                          active={sort.field === 'exchange_count'}
                          direction={
                            sort.field === 'exchange_count'
                              ? sort.direction
                              : 'desc'
                          }
                          onClick={() => handleSortChange('exchange_count')}
                        >
                          やりとり
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="center" sx={{ width: '10%' }}>
                        状態
                      </TableCell>
                      <TableCell align="center" sx={{ width: '8%' }}>
                        詳細
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metrics.map((metric) => (
                      <TableRow key={metric.id}>
                        <TableCell>
                          {formatDateTime(metric.created_at)}
                        </TableCell>
                        <TableCell
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {metric.pharmacy_name}
                        </TableCell>
                        <TableCell
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {metric.medical_institution}
                        </TableCell>
                        <TableCell>
                          {metric.prescription_number
                            ? `${metric.prescription_number.slice(
                                0,
                                METRICS_CONFIG.PRESCRIPTION_NUMBER_DISPLAY_LENGTH
                              )}...`
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {metric.medication_count != null
                            ? `${metric.medication_count}剤`
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {metric.has_audio
                            ? formatDuration(metric.duration_seconds)
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {metric.exchange_count != null
                            ? `${metric.exchange_count}回`
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {metric.soap_copied
                            ? 'コピー済み'
                            : metric.has_soap
                            ? 'SOAP生成済み'
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <ActionButton
                            variant="contained"
                            size="small"
                            onClick={() =>
                              navigate(`/portal/metrics/records/${metric.id}`)
                            }
                          >
                            詳細
                          </ActionButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {totalCount > METRICS_CONFIG.PAGE_SIZE && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={Math.ceil(totalCount / METRICS_CONFIG.PAGE_SIZE)}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </PortalLayout>
  )
}
